import { useMemo, useState } from "react";
import { Link, Trash2, X } from "lucide-react";
import { categories, iconOptions } from "../data/bookmarks";
import type { Bookmark, BookmarkDraft, BookmarkStatus } from "../types";
import { IconGlyph } from "./IconGlyph";
import { IconUpscalePanel } from "./IconUpscalePanel";

type BookmarkFormProps = {
  bookmark?: Bookmark | null;
  open: boolean;
  onClose: () => void;
  onSave: (bookmark: BookmarkDraft) => void;
  onDelete: (id: string) => void;
};

const emptyDraft: BookmarkDraft = {
  label: "",
  url: "",
  category: "Server",
  icon: "rocket",
  accent: "#7eb7ff",
  description: "",
  status: "unknown",
  customIconDataUrl: undefined,
  iconSourceUrl: undefined
};

const statuses: BookmarkStatus[] = ["protected", "local", "online", "unknown"];

export function BookmarkForm({ bookmark, open, onClose, onSave, onDelete }: BookmarkFormProps) {
  const [draft, setDraft] = useState<BookmarkDraft>(bookmark ?? emptyDraft);
  const [error, setError] = useState("");

  const title = useMemo(() => (bookmark ? "URLを編集" : "URLを登録"), [bookmark]);

  if (!open) return null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.label.trim()) {
      setError("ラベルを入力してください。");
      return;
    }
    try {
      const parsed = new URL(draft.url);
      if (!["http:", "https:"].includes(parsed.protocol)) throw new Error("invalid protocol");
    } catch {
      setError("http または https のURLを入力してください。");
      return;
    }
    onSave(draft);
    onClose();
  };

  return (
    <div className="drawer-backdrop" role="presentation">
      <aside className="bookmark-drawer" role="dialog" aria-modal="true" aria-labelledby="bookmark-form-title">
        <div className="drawer-heading">
          <h2 id="bookmark-form-title">
            <Link size={19} />
            {title}
          </h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="閉じる">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="bookmark-form">
          <label>
            ラベル
            <input
              value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
              placeholder="新しいブックマーク"
              autoFocus
            />
          </label>
          <label>
            URL
            <input
              value={draft.url}
              onChange={(event) => setDraft({ ...draft, url: event.target.value })}
              placeholder="https://example.com"
            />
          </label>
          <label>
            説明
            <input
              value={draft.description ?? ""}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              placeholder="短いメモ"
            />
          </label>

          <div className="form-row">
            <label>
              カテゴリ
              <select
                value={draft.category}
                onChange={(event) => setDraft({ ...draft, category: event.target.value as BookmarkDraft["category"] })}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
            <label>
              状態
              <select
                value={draft.status}
                onChange={(event) => setDraft({ ...draft, status: event.target.value as BookmarkStatus })}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            アクセント
            <input
              type="color"
              value={draft.accent}
              onChange={(event) => setDraft({ ...draft, accent: event.target.value })}
            />
          </label>

          <fieldset className="icon-picker">
            <legend>アイコン</legend>
            <div>
              {iconOptions.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  className={draft.icon === icon ? "selected" : ""}
                  onClick={() => setDraft({ ...draft, icon })}
                  aria-label={`${icon} アイコン`}
                >
                  <IconGlyph name={icon} size={22} />
                </button>
              ))}
            </div>
          </fieldset>

          <IconUpscalePanel
            pageUrl={draft.url}
            accent={draft.accent}
            customIconDataUrl={draft.customIconDataUrl}
            iconSourceUrl={draft.iconSourceUrl}
            onChange={(customIconDataUrl, iconSourceUrl) => setDraft({ ...draft, customIconDataUrl, iconSourceUrl })}
          />

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            {bookmark && (
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  onDelete(bookmark.id);
                  onClose();
                }}
              >
                <Trash2 size={18} />
                削除
              </button>
            )}
            <button type="button" className="secondary-button" onClick={onClose}>
              キャンセル
            </button>
            <button type="submit" className="primary-button">
              保存
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
